import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm }     from 'react-hook-form';
import { useQuery }    from 'react-query';
import { toast }       from 'react-toastify';
import api, { cereriAPI, adminAPI } from '../services/api';

const TIPURI = [
  { value: 'sesizare',    label: 'Sesizare' },
  { value: 'solicitare',  label: 'Solicitare' },
  { value: 'petitie',     label: 'Petiție' },
  { value: 'reclamatie',  label: 'Reclamație' },
  { value: 'informatie',  label: 'Cerere de informații' },
];

const ACCEPTATE = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx';

export default function CerereNovaPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [slotFiles, setSlotFiles]   = useState({}); // { reqId: File }
  const [extraFiles, setExtraFiles] = useState([]);

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();
  const tipSelectat  = watch('tip', '');
  const catSelectata = watch('categorie', '');

  const { data: categorii } = useQuery('categorii', adminAPI.getCategorii);

  const { data: docReqs } = useQuery(
    ['doc-reqs', tipSelectat, catSelectata],
    () => api.get(`/doc-req?tip_cerere=${tipSelectat}${catSelectata ? '&categorie=' + catSelectata : ''}`).then(r => r.data.data),
    { enabled: !!tipSelectat }
  );

  function setSlot(id, file) {
    setSlotFiles(prev => ({ ...prev, [id]: file }));
  }

  function onExtraChange(e) {
    const selected = Array.from(e.target.files);
    const valid = selected.filter(f => {
      if (f.size > 10 * 1024 * 1024) { toast.warning(`${f.name} depășește 10MB`); return false; }
      return true;
    });
    setExtraFiles(prev => [...prev, ...valid].slice(0, 5));
  }

  function removeExtra(idx) {
    setExtraFiles(prev => prev.filter((_, i) => i !== idx));
  }

  async function onSubmit(data) {
    // Validate required slots
    const lipsesc = (docReqs || []).filter(r => r.obligatoriu && !slotFiles[r.id]);
    if (lipsesc.length > 0) {
      toast.error(`Documente obligatorii lipsă: ${lipsesc.map(r => r.denumire).join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('titlu',     data.titlu);
      formData.append('descriere', data.descriere);
      formData.append('tip',       data.tip);
      formData.append('categorie', data.categorie);
      if (data.locatie) formData.append('locatie', data.locatie);

      // Build ordered file list + labels
      const allLabels = [];
      (docReqs || []).forEach(req => {
        if (slotFiles[req.id]) {
          formData.append('atasamente', slotFiles[req.id]);
          allLabels.push(req.denumire);
        }
      });
      extraFiles.forEach(f => {
        formData.append('atasamente', f);
        allLabels.push('');
      });
      formData.append('doc_labels', JSON.stringify(allLabels));

      const result = await cereriAPI.create(formData);
      toast.success(`Cererea ${result.data.numarCerere} a fost înregistrată!`);
      navigate(`/cereri/${result.data.id}`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Eroare la înregistrarea cererii';
      toast.error(msg);
      (err.response?.data?.errors || []).forEach(e => toast.warning(e.msg || e.message));
    } finally {
      setLoading(false);
    }
  }

  const hasDocReqs = (docReqs || []).length > 0;

  return (
    <div style={st.page}>
      <div style={st.header}>
        <button onClick={() => navigate(-1)} style={st.back}>← Înapoi</button>
        <h1 style={st.title}>Cerere nouă</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} style={st.form} noValidate>

        {/* Tip cerere */}
        <div style={st.field}>
          <label style={st.label}>Tipul cererii *</label>
          <select
            style={{ ...st.input, ...(errors.tip ? st.inputErr : {}) }}
            {...register('tip', { required: 'Selectează tipul cererii' })}
          >
            <option value="">Selectează...</option>
            {TIPURI.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {errors.tip && <span style={st.err}>{errors.tip.message}</span>}
        </div>

        {/* Categorie */}
        <div style={st.field}>
          <label style={st.label}>Categoria *</label>
          <select
            style={{ ...st.input, ...(errors.categorie ? st.inputErr : {}) }}
            {...register('categorie', { required: 'Selectează categoria' })}
          >
            <option value="">Selectează...</option>
            {(categorii || []).map(c => (
              <option key={c.id} value={c.cod}>{c.nume}</option>
            ))}
          </select>
          {errors.categorie && <span style={st.err}>{errors.categorie.message}</span>}
        </div>

        {/* Titlu */}
        <div style={st.field}>
          <label style={st.label}>Subiect / Titlu *</label>
          <input
            type="text"
            style={{ ...st.input, ...(errors.titlu ? st.inputErr : {}) }}
            placeholder="Descriere scurtă a problemei"
            {...register('titlu', {
              required: 'Titlul este obligatoriu',
              minLength: { value: 10, message: 'Minim 10 caractere' },
              maxLength: { value: 500, message: 'Maxim 500 caractere' },
            })}
          />
          {errors.titlu && <span style={st.err}>{errors.titlu.message}</span>}
        </div>

        {/* Descriere */}
        <div style={st.field}>
          <label style={st.label}>Descriere detaliată *</label>
          <textarea
            rows={6}
            style={{ ...st.input, ...st.textarea, ...(errors.descriere ? st.inputErr : {}) }}
            placeholder="Descrie problema în detaliu: locație, context, ce ai observat..."
            {...register('descriere', {
              required: 'Descrierea este obligatorie',
              minLength: { value: 20, message: 'Minim 20 caractere' },
              maxLength: { value: 5000, message: 'Maxim 5000 caractere' },
            })}
          />
          {errors.descriere && <span style={st.err}>{errors.descriere.message}</span>}
        </div>

        {/* Locație */}
        <div style={st.field}>
          <label style={st.label}>Adresă / Locație (opțional)</label>
          <input
            type="text"
            style={st.input}
            placeholder="ex: Str. Mihai Eminescu nr. 10, lângă parcul central"
            {...register('locatie')}
          />
        </div>

        {/* ── Documente configurate per tip cerere ── */}
        {hasDocReqs && (
          <div style={st.docsSection}>
            <div style={st.docsSectionTitle}>
              📎 Documente necesare
              <span style={st.docsSectionSub}>Atașați documentele solicitate pentru acest tip de cerere</span>
            </div>
            {docReqs.map((req, idx) => (
              <SlotUpload
                key={req.id}
                req={req}
                idx={idx}
                file={slotFiles[req.id]}
                onChange={file => setSlot(req.id, file)}
              />
            ))}
          </div>
        )}

        {/* ── Alte documente (atașamente libere) ── */}
        <div style={st.field}>
          <label style={st.label}>
            {hasDocReqs ? 'Alte documente (opțional)' : 'Atașamente (opțional)'}
          </label>
          <div style={st.uploadZone}>
            <input
              type="file"
              id="extra-files"
              multiple
              accept={ACCEPTATE}
              onChange={onExtraChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="extra-files" style={st.uploadLabel}>
              Selectează fișiere (PDF, JPG, PNG, DOC — max 10MB, max 5 fișiere)
            </label>
          </div>
          {extraFiles.length > 0 && (
            <div style={st.fileList}>
              {extraFiles.map((f, idx) => (
                <div key={idx} style={st.fileItem}>
                  <span style={st.fileName}>{f.name}</span>
                  <span style={st.fileSize}>{(f.size / 1024).toFixed(0)} KB</span>
                  <button type="button" onClick={() => removeExtra(idx)} style={st.removeBtn}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button type="submit" disabled={loading} style={st.submitBtn}>
          {loading ? 'Se înregistrează...' : 'Depune cererea'}
        </button>
      </form>
    </div>
  );
}

function SlotUpload({ req, idx, file, onChange }) {
  const inputRef = useRef();
  return (
    <div style={st.slot}>
      <div style={st.slotHeader}>
        <span style={st.slotNum}>D{idx + 1}</span>
        <div style={{ flex: 1 }}>
          <span style={st.slotName}>{req.denumire}</span>
          {req.obligatoriu
            ? <span style={st.badgeOblig}>obligatoriu</span>
            : <span style={st.badgeOpt}>opțional</span>}
        </div>
      </div>
      {req.descriere && <p style={st.slotDesc}>{req.descriere}</p>}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '8px' }}>
        <button type="button" onClick={() => inputRef.current?.click()} style={st.slotBtn}>
          {file ? `✓ ${file.name}` : '📁 Alege fișier'}
        </button>
        {file && (
          <>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>{(file.size / 1024).toFixed(0)} KB</span>
            <button type="button" onClick={() => onChange(null)} style={st.slotRemove}>×</button>
          </>
        )}
        <input ref={inputRef} type="file" accept={ACCEPTATE} style={{ display: 'none' }}
          onChange={e => onChange(e.target.files[0] || null)} />
      </div>
    </div>
  );
}

const st = {
  page:    { maxWidth: '680px', margin: '0 auto', padding: '24px 16px' },
  header:  { marginBottom: '24px' },
  back:    { background: 'none', border: 'none', cursor: 'pointer', color: '#2d2d7e', fontSize: '14px', padding: 0, marginBottom: '12px', display: 'block' },
  title:   { margin: 0, fontSize: '24px', fontWeight: '700', color: '#1a1a2e' },
  form:    { display: 'flex', flexDirection: 'column', gap: '20px' },
  field:   { display: 'flex', flexDirection: 'column', gap: '6px' },
  label:   { fontSize: '14px', fontWeight: '500', color: '#333' },
  input: {
    padding: '10px 14px', fontSize: '16px',
    border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none',
    fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
    WebkitAppearance: 'none',
  },
  inputErr: { borderColor: '#e24b4a' },
  textarea:  { resize: 'vertical', minHeight: '120px' },
  err:       { fontSize: '12px', color: '#e24b4a' },
  // Upload zone
  uploadZone:  { border: '2px dashed #ddd', borderRadius: '8px', padding: '24px', textAlign: 'center' },
  uploadLabel: { cursor: 'pointer', color: '#2d2d7e', fontSize: '14px', fontWeight: '500' },
  fileList:    { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' },
  fileItem:    { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '6px' },
  fileName:    { flex: 1, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileSize:    { fontSize: '12px', color: '#888', flexShrink: 0 },
  removeBtn:   { background: 'none', border: 'none', cursor: 'pointer', color: '#e24b4a', fontSize: '18px', padding: '0 4px', lineHeight: 1 },
  // Doc slots section
  docsSection:      { background: '#f8faff', border: '1.5px solid #dde3f5', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  docsSectionTitle: { fontWeight: '600', fontSize: '14px', color: '#1a1a2e', display: 'flex', flexDirection: 'column', gap: '2px' },
  docsSectionSub:   { fontSize: '12px', color: '#6b7280', fontWeight: '400' },
  // Slot
  slot:        { background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '12px' },
  slotHeader:  { display: 'flex', alignItems: 'flex-start', gap: '10px' },
  slotNum:     { background: '#2d2d7e', color: '#fff', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: '700', flexShrink: 0, marginTop: '2px' },
  slotName:    { fontWeight: '600', fontSize: '13px', color: '#1a1a2e', marginRight: '6px' },
  slotDesc:    { fontSize: '12px', color: '#6b7280', margin: '4px 0 0' },
  badgeOblig:  { fontSize: '10px', background: '#fee2e2', color: '#dc2626', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' },
  badgeOpt:    { fontSize: '10px', background: '#f0fdf4', color: '#15803d', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' },
  slotBtn:     { padding: '7px 14px', border: '1.5px dashed #d1d5db', borderRadius: '7px', background: '#f9fafb', cursor: 'pointer', fontSize: '13px', color: '#374151', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  slotRemove:  { background: 'none', border: 'none', cursor: 'pointer', color: '#e24b4a', fontSize: '18px', padding: '0 4px', lineHeight: 1 },
  // Submit
  submitBtn: {
    padding: '14px', backgroundColor: '#2d2d7e', color: '#fff',
    border: 'none', borderRadius: '8px', fontSize: '16px',
    fontWeight: '600', cursor: 'pointer', marginTop: '8px',
  },
};
